import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
};

// Business APIs
export const businessApi = {
  getAll: () => api.get('/businesses'),
  create: (data) => api.post('/businesses', data),
  update: (id, data) => api.put(`/businesses/${id}`, data),
  delete: (id) => api.delete(`/businesses/${id}`),
};

// User APIs
export const userApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Role APIs
export const roleApi = {
  getAll: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

// Loyverse Data APIs
export const loyverseApi = {
  getStores: (businessId) => api.get(`/businesses/${businessId}/stores`),
  getBranches: (businessId) => api.get(`/businesses/${businessId}/branches`),
  getEmployees: (businessId) => api.get(`/businesses/${businessId}/employees`),
  getItems: (businessId) => api.get(`/businesses/${businessId}/items`),
  getCategories: (businessId) => api.get(`/businesses/${businessId}/categories`),
  getModifiers: (businessId) => api.get(`/businesses/${businessId}/modifiers`),
  getDiscounts: (businessId) => api.get(`/businesses/${businessId}/discounts`),
  getPaymentTypes: (businessId) => api.get(`/businesses/${businessId}/payment-types`),
  getPosDevices: (businessId) => api.get(`/businesses/${businessId}/pos-devices`),
  getReceipts: (businessId, params) => api.get(`/businesses/${businessId}/receipts`, { params }),
  getDashboard: (businessId, filters) => api.post(`/businesses/${businessId}/dashboard`, filters),
  // Sync APIs
  getSyncStatus: (businessId) => api.get(`/businesses/${businessId}/sync/status`),
  startSync: (businessId) => api.post(`/businesses/${businessId}/sync`),
  // AI Insights
  getInsights: (businessId, data) => api.post(`/businesses/${businessId}/insights`, data),
};

// Saved Layouts APIs
export const layoutApi = {
  getAll: () => api.get('/saved-layouts'),
  create: (data) => api.post('/saved-layouts', data),
  delete: (id) => api.delete(`/saved-layouts/${id}`),
};

// Target APIs
export const targetApi = {
  getAll: (businessId, params) => api.get(`/businesses/${businessId}/targets`, { params }),
  create: (businessId, data) => api.post(`/businesses/${businessId}/targets`, data),
  update: (businessId, targetId, data) => api.put(`/businesses/${businessId}/targets/${targetId}`, data),
  delete: (businessId, targetId) => api.delete(`/businesses/${businessId}/targets/${targetId}`),
  suggest: (businessId, params) => api.get(`/businesses/${businessId}/targets/suggest`, { params }),
};

export default api;
